import axios from '@/core/axios';

import {
  Quote,
  QuoteDownload,
  QuoteItem,
  QuoteReservedDevice,
  QuoteSerialLookup,
  QuoteStatusOption
} from './quotes.types';

export class QuotesService {
  /* Normalize backend field name differences:
   * - validity_duration_days → validity_duration
   * - quote_items            → items
   */
  private normalize(data: any): Quote {
    if (!data) return data;
    if (data.validity_duration == null && data.validity_duration_days != null) {
      data.validity_duration = data.validity_duration_days;
    }
    if (!data.items && data.quote_items) {
      data.items = data.quote_items;
    }
    return data as Quote;
  }

  /* Reverse of normalize: translate frontend field names back to backend field names before sending */
  private serialize(quote: Partial<Quote>): any {
    const payload: any = { ...quote };
    if ('validity_duration' in payload) {
      payload.validity_duration_days = payload.validity_duration;
      delete payload.validity_duration;
    }
    return payload;
  }

  /* Returns a blank quote template (defaults, billing info prefilled from the customer) */
  new(customerId?: number | string): Promise<Quote> {
    return axios.get<Quote>('/quotes/new', { params: { customer_id: customerId } }).then(resp => resp?.data);
  }

  statuses(): Promise<QuoteStatusOption[]> {
    return axios.get<QuoteStatusOption[]>('/quotes/statuses').then(resp => resp?.data || []);
  }

  validityDurations(): Promise<number[]> {
    return axios.get<number[]>('/quotes/validity-durations').then(resp => resp?.data || []);
  }

  paymentConditions(): Promise<string[]> {
    return axios.get<string[]>('/quotes/payment-conditions').then(resp => resp?.data || []);
  }

  listForCustomer(customerId: number | string): Promise<Quote[]> {
    if (!customerId) return Promise.resolve([]);
    return axios.get<any>(`/customers/${customerId}/quotes`).then(resp => {
      const data = resp?.data;
      if (Array.isArray(data)) return data as Quote[];
      if (data?.values) return data.values as Quote[];
      return [];
    });
  }

  get(quoteId: number | string): Promise<Quote> {
    if (!quoteId) throw new Error('Missing quote id in get');
    return axios.get<Quote>(`/quotes/${quoteId}`).then(resp => this.normalize(resp?.data));
  }

  create(quote: Partial<Quote>): Promise<Quote> {
    return axios.post<Quote>('/quotes', { quote: this.serialize(quote) }).then(resp => this.normalize(resp?.data));
  }

  update(quoteId: number | string, quote: Partial<Quote>): Promise<Quote> {
    if (!quoteId) throw new Error('Missing quote id in update');
    return axios
      .put<Quote>(`/quotes/${quoteId}`, { quote: this.serialize(quote) })
      .then(resp => this.normalize(resp?.data));
  }

  delete(quoteId: number | string): Promise<void> {
    if (!quoteId) throw new Error('Missing quote id in delete');
    return axios.delete<void>(`/quotes/${quoteId}`).then(resp => resp?.data);
  }

  validate(quoteId: number | string): Promise<Quote> {
    if (!quoteId) throw new Error('Missing quote id in validate');
    return axios.post<Quote>(`/quotes/${quoteId}/validate`).then(resp => this.normalize(resp?.data));
  }

  refuse(quoteId: number | string): Promise<Quote> {
    if (!quoteId) throw new Error('Missing quote id in refuse');
    return axios.post<Quote>(`/quotes/${quoteId}/refuse`).then(resp => this.normalize(resp?.data));
  }

  uploadSigned(quoteId: number | string, file: File): Promise<Quote> {
    if (!quoteId) throw new Error('Missing quote id in uploadSigned');
    const formData = new FormData();
    formData.append('signed_pdf', file);
    return axios
      .post<Quote>(`/quotes/${quoteId}/upload-signed`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(resp => this.normalize(resp?.data));
  }

  accept(quoteId: number | string): Promise<Quote> {
    if (!quoteId) throw new Error('Missing quote id in accept');
    return axios.post<Quote>(`/quotes/${quoteId}/accept`).then(resp => this.normalize(resp?.data));
  }

  /* Crée manuellement la commande d'un devis accepté dont la création automatique
   * a été différée (paiement à la commande / acompte) — cf. order_creation_pending */
  createOrder(quoteId: number | string): Promise<Quote> {
    if (!quoteId) throw new Error('Missing quote id in createOrder');
    return axios.post<Quote>(`/quotes/${quoteId}/create-order`).then(resp => this.normalize(resp?.data));
  }

  /* Duplique un devis (validated/accepted/refused/deleted) en un nouveau brouillon.
   * transferReservations : transfère les devices encore réservés vers la copie. */
  duplicate(quoteId: number | string, transferReservations: boolean): Promise<Quote> {
    if (!quoteId) throw new Error('Missing quote id in duplicate');
    return axios
      .post<Quote>(`/quotes/${quoteId}/duplicate`, { transfer_reservations: transferReservations })
      .then(resp => this.normalize(resp?.data));
  }

  /* PDF downloads — backend returns [{ base_64, type, filename }] */
  download(quoteId: number | string): Promise<QuoteDownload> {
    if (!quoteId) throw new Error('Missing quote id in download');
    return axios
      .get<QuoteDownload | QuoteDownload[]>(`/quotes/${quoteId}/download`)
      .then(resp => (Array.isArray(resp?.data) ? resp.data[0] : resp?.data));
  }

  downloadSigned(quoteId: number | string): Promise<QuoteDownload> {
    if (!quoteId) throw new Error('Missing quote id in downloadSigned');
    return axios
      .get<QuoteDownload | QuoteDownload[]>(`/quotes/${quoteId}/download-signed`)
      .then(resp => (Array.isArray(resp?.data) ? resp.data[0] : resp?.data));
  }

  /* Nested quote items */
  listItems(quoteId: number | string): Promise<QuoteItem[]> {
    return axios.get<QuoteItem[]>(`/quotes/${quoteId}/items`).then(resp => resp?.data || []);
  }

  createItem(quoteId: number | string, item: Partial<QuoteItem>): Promise<QuoteItem> {
    return axios.post<QuoteItem>(`/quotes/${quoteId}/items`, { quote_item: item }).then(resp => resp?.data);
  }

  /* Recherche d'un device vendable (ou déjà "Réservé BtoB") par numéro de série,
   * pour l'ajout d'un article de devis "par numéro de série" */
  lookupSerial(quoteId: number | string, serialNo: string): Promise<QuoteSerialLookup> {
    return axios
      .get<QuoteSerialLookup>(`/quotes/${quoteId}/lookup-serial/${encodeURIComponent(serialNo)}`)
      .then(resp => resp?.data);
  }

  /* Ajoute (ou incrémente si le SKU de référence est déjà présent sur le devis)
   * une ligne à partir d'un numéro de série, et réserve immédiatement ce device
   * sur la ligne (équivalent à l'onglet "Éléments réservés") */
  createItemFromSerial(quoteId: number | string, item: Partial<QuoteItem> & { serial_no: string }): Promise<QuoteItem> {
    return axios.post<QuoteItem>(`/quotes/${quoteId}/items/from-serial`, { quote_item: item }).then(resp => resp?.data);
  }

  updateItem(quoteId: number | string, itemId: number | string, item: Partial<QuoteItem>): Promise<QuoteItem> {
    return axios.put<QuoteItem>(`/quotes/${quoteId}/items/${itemId}`, { quote_item: item }).then(resp => resp?.data);
  }

  deleteItem(quoteId: number | string, itemId: number | string): Promise<void> {
    return axios.delete<void>(`/quotes/${quoteId}/items/${itemId}`).then(resp => resp?.data);
  }

  /* Device reservation on a quote item (device lines only) */
  availableDevices(itemId: number | string): Promise<QuoteReservedDevice[]> {
    return axios.get<QuoteReservedDevice[]>(`/quote-items/${itemId}/available-devices`).then(resp => resp?.data || []);
  }

  reservedDevices(itemId: number | string): Promise<QuoteReservedDevice[]> {
    return axios.get<QuoteReservedDevice[]>(`/quote-items/${itemId}/devices`).then(resp => resp?.data || []);
  }

  reserveDevices(itemId: number | string, deviceIds: number[]): Promise<QuoteReservedDevice[]> {
    return axios
      .post<QuoteReservedDevice[]>(`/quote-items/${itemId}/devices`, { device_ids: deviceIds })
      .then(resp => resp?.data || []);
  }

  releaseDevice(itemId: number | string, deviceId: number | string): Promise<QuoteReservedDevice[]> {
    return axios
      .delete<QuoteReservedDevice[]>(`/quote-items/${itemId}/devices/${deviceId}`)
      .then(resp => resp?.data || []);
  }
}

export const quotesService = new QuotesService();
